export interface BusinessCase {
  slug: string;
  profileLabel: string;
  profileLabelEn: string;
  title: string;
  titleEn: string;
  metrics: { value: string; label: string; labelEn: string }[];
  challenge: string;
  challengeEn: string;
  strategy: string[];
  strategyEn: string[];
  results: string[];
  resultsEn: string[];
}

export const businessCases: BusinessCase[] = [
  {
    slug: "reestructuracion-deuda-sobrestock",
    profileLabel: "Cadena de moda · 15 tiendas",
    profileLabelEn: "Fashion chain · 15 stores",
    title: "Reestructuración de deuda y liquidación de 2M€ en sobrestock.",
    titleEn: "Debt restructuring and liquidation of €2M in overstock.",
    metrics: [
      { value: "+14%", label: "EBITDA", labelEn: "EBITDA" },
      { value: "-30%", label: "Stock inmovilizado", labelEn: "Immobilized stock" },
      { value: "8 meses", label: "Tiempo de ejecución", labelEn: "Execution time" },
    ],
    challenge:
      "La cadena arrastraba un sobrestock valorado en más de 2M€ acumulado durante 3 temporadas. El flujo de caja estaba comprometido y la relación con proveedores se había deteriorado. La deuda con entidades financieras superaba los 800K€ y el margen operativo caía un 4% interanual.",
    challengeEn:
      "The chain had accumulated overstock worth over €2M across 3 seasons. Cash flow was compromised and supplier relationships had deteriorated. Debt with financial institutions exceeded €800K and operating margin was falling 4% year-over-year.",
    strategy: [
      "Auditoría completa de inventario por categoría, antigüedad y rotación real.",
      "Diseño de un plan de liquidación escalonado con 3 canales: outlet propio, marketplace B2B y acuerdos de liquidación con operadores especializados.",
      "Negociación con entidades financieras para reestructurar deuda a 36 meses con carencia de 6.",
      "Implantación de un modelo de compras basado en Open-to-Buy para evitar futuras acumulaciones.",
    ],
    strategyEn: [
      "Complete inventory audit by category, age, and actual turnover.",
      "Design of a phased liquidation plan with 3 channels: own outlet, B2B marketplace, and liquidation agreements with specialized operators.",
      "Negotiation with financial institutions to restructure debt over 36 months with a 6-month grace period.",
      "Implementation of an Open-to-Buy purchasing model to prevent future accumulations.",
    ],
    results: [
      "EBITDA incrementado un 14% en el primer ejercicio completo.",
      "Stock inmovilizado reducido un 30% en 8 meses.",
      "Deuda financiera reestructurada con ahorro de 95K€ en intereses.",
      "Ratio de rotación de inventario mejorado de 2,1 a 3,4 vueltas/año.",
    ],
    resultsEn: [
      "EBITDA increased by 14% in the first full fiscal year.",
      "Immobilized stock reduced by 30% in 8 months.",
      "Financial debt restructured with €95K savings in interest.",
      "Inventory turnover ratio improved from 2.1 to 3.4 turns/year.",
    ],
  },
  {
    slug: "expansion-multimarca-5-aperturas",
    profileLabel: "Operador multimarca · 8 tiendas",
    profileLabelEn: "Multi-brand operator · 8 stores",
    title: "Plan de expansión rentable: de 8 a 13 puntos de venta en 18 meses.",
    titleEn: "Profitable expansion plan: from 8 to 13 points of sale in 18 months.",
    metrics: [
      { value: "+5", label: "Nuevas aperturas", labelEn: "New openings" },
      { value: "+22%", label: "Facturación total", labelEn: "Total revenue" },
      { value: "ROI 2,8x", label: "Retorno inversión", labelEn: "Return on investment" },
    ],
    challenge:
      "El operador quería crecer pero carecía de un modelo financiero sólido para evaluar nuevas ubicaciones. Las 2 últimas aperturas habían sido deficitarias y la dirección había perdido confianza en su proceso de selección de locales.",
    challengeEn:
      "The operator wanted to grow but lacked a solid financial model to evaluate new locations. The last 2 openings had been unprofitable and management had lost confidence in their site selection process.",
    strategy: [
      "Desarrollo de un modelo de viabilidad por local con 14 variables clave (demografía, competencia, coste de ocupación, etc.).",
      "Scoring de 22 ubicaciones candidatas y selección de las 5 con mayor potencial ajustado al riesgo.",
      "Negociación de contratos de alquiler con cláusulas de salida flexible y rentas escalonadas.",
      "Diseño de un playbook operativo para reducir el time-to-break-even de nuevas tiendas.",
    ],
    strategyEn: [
      "Development of a per-location viability model with 14 key variables (demographics, competition, occupancy cost, etc.).",
      "Scoring of 22 candidate locations and selection of the 5 with highest risk-adjusted potential.",
      "Lease negotiation with flexible exit clauses and graduated rents.",
      "Design of an operational playbook to reduce time-to-break-even for new stores.",
    ],
    results: [
      "5 nuevas aperturas con un ROI medio de 2,8x en el primer año.",
      "Facturación total del grupo incrementada un 22%.",
      "Tiempo medio hasta el punto de equilibrio reducido de 14 a 7 meses.",
      "0 cierres forzados en las nuevas ubicaciones tras 18 meses de operación.",
    ],
    resultsEn: [
      "5 new openings with an average ROI of 2.8x in the first year.",
      "Total group revenue increased by 22%.",
      "Average time to break-even reduced from 14 to 7 months.",
      "0 forced closures in new locations after 18 months of operation.",
    ],
  },
  {
    slug: "due-diligence-adquisicion-retail",
    profileLabel: "Family Office · Inversión retail",
    profileLabelEn: "Family Office · Retail investment",
    title: "Due diligence operativa para la adquisición de una cadena de 25 tiendas.",
    titleEn: "Operational due diligence for the acquisition of a 25-store chain.",
    metrics: [
      { value: "-18%", label: "Precio renegociado", labelEn: "Renegotiated price" },
      { value: "3,2M€", label: "Valor oculto detectado", labelEn: "Hidden value detected" },
      { value: "45 días", label: "Plazo de entrega", labelEn: "Delivery time" },
    ],
    challenge:
      "Un Family Office evaluaba la adquisición de una cadena de retail valorada en 12M€. Necesitaban una visión independiente y experta del negocio más allá de los números contables para identificar riesgos ocultos y oportunidades de mejora operativa.",
    challengeEn:
      "A Family Office was evaluating the acquisition of a retail chain valued at €12M. They needed an independent, expert view of the business beyond accounting numbers to identify hidden risks and operational improvement opportunities.",
    strategy: [
      "Análisis profundo de unit economics por tienda: ventas/m², coste de personal vs. facturación, y productividad real.",
      "Auditoría de contratos de alquiler, proveedores estratégicos y compromisos de compra futuros.",
      "Evaluación del equipo directivo y la cultura operativa mediante entrevistas y observación in-situ.",
      "Elaboración de un informe de 'valor oculto' con quick wins implementables en los primeros 90 días post-adquisición.",
    ],
    strategyEn: [
      "Deep analysis of unit economics per store: sales/m², staff cost vs. revenue, and actual productivity.",
      "Audit of lease contracts, strategic suppliers, and future purchase commitments.",
      "Evaluation of the management team and operational culture through interviews and on-site observation.",
      "Preparation of a 'hidden value' report with quick wins implementable in the first 90 days post-acquisition.",
    ],
    results: [
      "Precio de adquisición renegociado a la baja un 18% gracias a los hallazgos del informe.",
      "3,2M€ en valor oculto identificado (renegociación de alquileres, optimización de plantilla, mejora de mix de producto).",
      "Plan de 90 días post-cierre que generó un incremento del 9% en margen bruto.",
      "Informe entregado en 45 días, dentro del plazo de exclusividad del deal.",
    ],
    resultsEn: [
      "Acquisition price renegotiated down 18% thanks to report findings.",
      "€3.2M in hidden value identified (lease renegotiation, staff optimization, product mix improvement).",
      "90-day post-close plan that generated a 9% increase in gross margin.",
      "Report delivered in 45 days, within the deal's exclusivity period.",
    ],
  },
  {
    slug: "optimizacion-margenes-calzado",
    profileLabel: "Cadena de calzado · 22 tiendas",
    profileLabelEn: "Footwear chain · 22 stores",
    title: "Recuperación de 4 puntos de margen bruto en una cadena de calzado.",
    titleEn: "Recovery of 4 gross margin points in a footwear chain.",
    metrics: [
      { value: "+4pp", label: "Margen bruto", labelEn: "Gross margin" },
      { value: "-40%", label: "Merma por rebajas", labelEn: "Markdown loss" },
      { value: "320K€", label: "Ahorro anual", labelEn: "Annual savings" },
    ],
    challenge:
      "La cadena sufría una erosión continua de márgenes por una política de rebajas agresiva y falta de control sobre los escandallos de producto. El margen bruto había caído del 52% al 46% en tres ejercicios.",
    challengeEn:
      "The chain was suffering continuous margin erosion due to aggressive markdown policies and lack of control over product costings. Gross margin had fallen from 52% to 46% over three fiscal years.",
    strategy: [
      "Revisión completa de escandallos: renegociación con 8 proveedores clave y búsqueda de fuentes alternativas.",
      "Implantación de un modelo de gestión de rebajas por fases con reglas de activación basadas en semanas de cobertura.",
      "Formación del equipo de compras en técnicas de negociación y análisis de costes landed.",
      "Desarrollo de un dashboard de control de márgenes con alertas automáticas por categoría.",
    ],
    strategyEn: [
      "Complete review of costings: renegotiation with 8 key suppliers and search for alternative sources.",
      "Implementation of a phased markdown management model with activation rules based on weeks of cover.",
      "Training the buying team in negotiation techniques and landed cost analysis.",
      "Development of a margin control dashboard with automatic alerts by category.",
    ],
    results: [
      "Margen bruto recuperado del 46% al 50% (+4 puntos porcentuales).",
      "Merma por rebajas reducida un 40% respecto al ejercicio anterior.",
      "Ahorro anual estimado de 320K€ en coste de producto.",
      "Equipo de compras capacitado y operando con el nuevo modelo de forma autónoma.",
    ],
    resultsEn: [
      "Gross margin recovered from 46% to 50% (+4 percentage points).",
      "Markdown loss reduced by 40% compared to previous fiscal year.",
      "Estimated annual savings of €320K in product cost.",
      "Buying team trained and operating the new model autonomously.",
    ],
  },
];
